/* eslint-disable no-console */
import { throttle } from 'lodash-es';
import { SECOND } from '../../constants';
import type { BetslipEvent } from '../../types/betslip';

// Same key and raw `Record<string, BetslipEvent>` shape as the previous useLocalStorage-based
// persistence — existing users' stored betslips (and e2e localStorage seeding) must keep working.
const STORAGE_KEY = 'betslip';
const PERSIST_THROTTLE_TIME = 3 * SECOND;

export type BetslipRecord = Record<string, BetslipEvent>;

export const loadStoredBets = (): BetslipRecord => {
  try {
    const item = localStorage.getItem(STORAGE_KEY);
    return item ? (JSON.parse(item) as BetslipRecord) : {};
  } catch (error) {
    console.warn(`Error reading localStorage key “${STORAGE_KEY}”:`, error);
    return {};
  }
};

const write = (bets: BetslipRecord) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bets));
  } catch (error) {
    console.warn(`Error setting localStorage key "${STORAGE_KEY}":`, error);
  }
};

const throttledWrite = throttle(write, PERSIST_THROTTLE_TIME);

export const cancelPendingBetsPersistence = () => throttledWrite.cancel();
export const flushPendingBetsPersistence = () => throttledWrite.flush();

// User actions persist immediately; high-frequency socket updates are throttled. Cancelling the
// pending throttled write on an immediate one prevents a stale snapshot from overwriting it later.
export const persistBets = (bets: BetslipRecord, mode: 'immediate' | 'throttled') => {
  if (mode === 'immediate') {
    cancelPendingBetsPersistence();
    write(bets);
  } else {
    throttledWrite(bets);
  }
};

export const clearStoredBets = () => {
  cancelPendingBetsPersistence();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn(`Error removing localStorage key “${STORAGE_KEY}”:`, error);
  }
};
